import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Check, MoreHorizontal, Clock, Loader2, Archive, Copy, Trash2, Maximize2, Minimize2, Plus, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription } from "@/components/ui/alert-dialog";
import { ConfirmDialog } from "@/components/tasks/ConfirmDialog";
import { TagInput } from "@/components/tasks/TagInput";
import { ReminderPicker } from "@/components/tasks/ReminderPicker";
import { DatePicker, TimePicker } from "@/components/tasks/DateTimePicker";
import { TaskTimerControls } from "@/components/tasks/TaskTimerControls";
import { SubtaskList } from "@/components/tasks/SubtaskList";

const PRIORITIES = ["low", "normal", "high", "urgent"];
const uid = () => Math.random().toString(36).slice(2, 10);

const snapshot = (x = {}) => ({
  title: x.title || "", description: x.description || "", owner_id: x.owner_id || "",
  due_date: x.due_date || "", due_time: x.due_time || "", priority: x.priority || "normal",
  category_id: x.category_id || "", board_id: x.board_id || "", stage: x.stage || "", stage_id: x.stage_id || "",
  visibility: x.visibility || "personal", waiting: !!x.waiting,
  estimate_minutes: x.estimate_minutes ?? "", tags: x.tags || [], subtasks: x.subtasks || [],
  reminders: x.reminders || [], recurrence: x.recurrence || {},
});

// Canonical reusable Task Drawer — one component for BOTH create and edit.
// Explicit save (no autosave). Edit persists via PATCH (only changed fields).
export function TaskDrawer({ mode = "edit", taskId, initial = {}, defaults = {}, cats = [], boards = [], members = [], canAssign = false, onClose, onChanged, onCreated }) {
  const isCreate = mode === "create";
  const initialSnap = () => snapshot({ ...defaults, title: initial.title, due_date: initial.due_date || defaults.due_date, owner_id: initial.owner_id, priority: initial.priority, board_id: defaults.board_id, stage: defaults.stage, stage_id: defaults.stage_id });
  const [f, setF] = useState(() => isCreate ? initialSnap() : null);
  const [orig, setOrig] = useState(() => isCreate ? initialSnap() : null);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [hideEmpty, setHideEmpty] = useState(false);
  const [revealed, setRevealed] = useState(() => new Set());
  const [unsaved, setUnsaved] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [titleErr, setTitleErr] = useState(false);
  const [meta, setMeta] = useState(isCreate ? {} : null);   // extra read-only info (status, recurrence, related)

  useEffect(() => {
    if (isCreate || !taskId) return;
    setLoading(true);
    apiFetch(`/tasks/${taskId}`).then((found) => {
      if (found) { setF(snapshot(found)); setOrig(snapshot(found)); setMeta(found); }
    }).catch((e) => toast.error("Could not load task", { description: e.message })).finally(() => setLoading(false));
  }, [taskId, isCreate]);

  // Dirty = current form differs from the snapshot taken at mount (create) or
  // from the loaded record (edit). Prefilled defaults are part of the snapshot,
  // so opening Full Create from a prefilled route is NOT falsely "unsaved".
  const dirty = useMemo(() => f && orig && JSON.stringify(f) !== JSON.stringify(orig), [f, orig]);

  // Nested Escape: ignore while a Radix popper (select/popover/dropdown) or a
  // confirmation/unsaved dialog is open — those consume their own Escape first.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (unsaved || confirm) return;
      if (document.querySelector("[data-radix-popper-content-wrapper]")) return;
      if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) return;
      attemptClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, unsaved, confirm]);

  // Safety net: never leave the body locked after the drawer (and its nested
  // Radix overlays) unmount. Guarantees the app is clickable again on close.
  useEffect(() => () => {
    document.body.style.pointerEvents = "";
    document.body.style.overflow = "";
    document.body.removeAttribute("data-scroll-locked");
  }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const attemptClose = () => { if (dirty) setUnsaved(true); else onClose(); };
  const board = boards.find((b) => b.id === f?.board_id);

  const buildPatch = () => {
    const patch = {};
    for (const k of Object.keys(f)) {
      const a = f[k], b = orig[k];
      const av = a && typeof a === "object" ? JSON.stringify(a) : a;
      const bv = b && typeof b === "object" ? JSON.stringify(b) : b;
      if (av !== bv) patch[k] = k === "estimate_minutes" ? (a === "" ? null : Number(a)) : a;
    }
    return patch;
  };

  const save = async () => {
    if (!f.title.trim()) { setTitleErr(true); toast.error("Task title is required."); return false; }
    setSaving(true);
    try {
      if (isCreate) {
        const body = {
          ...defaults, title: f.title.trim(), description: f.description || "",
          due_date: f.due_date || null, due_time: f.due_time || null, priority: f.priority,
          category_id: f.category_id || "", board_id: f.board_id || "",
          stage_id: f.board_id ? (f.stage_id || board?.stages?.[0]?.id || "") : "",
          tags: f.tags, subtasks: f.subtasks, reminders: f.reminders, recurrence: f.recurrence,
          estimate_minutes: f.estimate_minutes ? Number(f.estimate_minutes) : null,
          task_type: f.owner_id ? "assigned" : (defaults.task_type || "personal"),
          owner_id: f.owner_id || undefined,
        };
        const t = await apiFetch("/tasks", { method: "POST", body: JSON.stringify(body) });
        toast.success("Task created"); onCreated?.(t); return true;
      }
      const patch = buildPatch();
      let updated = meta;
      if (Object.keys(patch).length) updated = await apiFetch(`/tasks/${taskId}`, { method: "PATCH", body: JSON.stringify(patch) });
      setF(snapshot(updated)); setOrig(snapshot(updated)); setMeta(updated);
      toast.success("Task updated"); onChanged?.(); return true;
    } catch (e) { toast.error(isCreate ? "Could not create task" : "Could not save changes", { description: e.message }); return false; }
    finally { setSaving(false); }
  };
  const saveAndClose = async () => { const ok = await save(); if (ok) { setUnsaved(false); onClose(); } };
  // Close the unsaved dialog first, then unmount the drawer on the next tick so
  // Radix can run its overlay cleanup (avoids a stale pointer-events lock).
  const discardAndClose = () => { setUnsaved(false); setTimeout(onClose, 0); };

  const toggleComplete = async () => {
    const done = meta.status === "done";
    try {
      await apiFetch(`/tasks/${taskId}/${done ? "reopen" : "complete"}`, { method: "PUT" });
      setMeta((m) => ({ ...m, status: done ? "open" : "done" }));
      onChanged?.();
      if (!done) toast.success("Task completed", { action: { label: "Undo", onClick: async () => {
        try { await apiFetch(`/tasks/${taskId}/reopen`, { method: "PUT" }); setMeta((m) => ({ ...m, status: "open" })); onChanged?.(); } catch (e) { toast.error(e.message); }
      } } });
    } catch (e) { toast.error(e.message); }
  };

  const doArchive = async () => { setBusy(true); try { await apiFetch(`/tasks/${taskId}/archive`, { method: "PUT", body: JSON.stringify({ archived: true }) }); toast.success("Task archived"); onChanged?.(); setConfirm(null); setTimeout(onClose, 0); } catch (e) { toast.error("Could not archive task", { description: e.message }); } finally { setBusy(false); } };
  const doDelete = async () => { setBusy(true); try { await apiFetch(`/tasks/${taskId}`, { method: "DELETE" }); toast.success("Task deleted"); onChanged?.(); setConfirm(null); setTimeout(onClose, 0); } catch (e) { toast.error("Could not delete task", { description: e.message }); } finally { setBusy(false); } };
  const doDuplicate = async () => { try { await apiFetch(`/tasks/${taskId}/duplicate`, { method: "POST" }); toast.success("Task duplicated"); onChanged?.(); } catch (e) { toast.error("Could not duplicate task", { description: e.message }); } };

  const addSub = (title) => { const v = (title || "").trim(); if (!v) return; set("subtasks", [...f.subtasks, { id: uid(), title: v, done: false }]); };
  const toggleSub = (id) => set("subtasks", f.subtasks.map((s) => (s.id === id ? { ...s, done: !s.done } : s)));
  const removeSub = (id) => set("subtasks", f.subtasks.filter((s) => s.id !== id));
  const renameSub = (id, title) => set("subtasks", f.subtasks.map((s) => (s.id === id ? { ...s, title } : s)));

  const canHardDelete = meta && ["personal", "reminder"].includes(meta.task_type) && !meta.related_id;
  const isDone = meta?.status === "done";

  // Hide-empty helper (edit only). Required + valued fields always show.
  const OPTIONAL = { description: f?.description, category_id: f?.category_id, board_id: f?.board_id, tags: f?.tags?.length, estimate_minutes: f?.estimate_minutes, due_time: f?.due_time };
  const LABELS = { description: "Notes", category_id: "Category", board_id: "Board", tags: "Tags", estimate_minutes: "Estimate", due_time: "Due time" };
  const show = (key) => !hideEmpty || !!OPTIONAL[key] || revealed.has(key);
  const hiddenKeys = Object.keys(OPTIONAL).filter((k) => !OPTIONAL[k] && !revealed.has(k));
  const reveal = (k) => setRevealed((s) => new Set(s).add(k));

  return createPortal(
    <div className="fixed inset-0 z-drawer" data-testid="task-drawer">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-200" onClick={attemptClose} />
      <aside className={cn(
        "absolute inset-y-0 right-0 flex w-full flex-col border-l border-border bg-card shadow-lift animate-in slide-in-from-right duration-300 max-sm:inset-0 max-sm:border-l-0",
        expanded ? "sm:w-[75vw] sm:max-w-6xl" : "sm:w-[560px] lg:w-[600px]")}>
        {loading ? (
          <div className="flex flex-1 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div>
        ) : (
          <>
            {/* STICKY HEADER */}
            <header className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-card/95 px-5 backdrop-blur-md">
              <div className="flex min-w-0 items-center gap-3">
                {isCreate ? (
                  <span className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground"><PlusCircle className="h-5 w-5 text-accent" /> New task</span>
                ) : (
                  <>
                    <button onClick={toggleComplete} data-testid="drawer-complete"
                      className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors", isDone ? "border-success bg-success text-white" : "border-border hover:border-accent hover:bg-accent-subtle")}>
                      <Check className={cn("h-3.5 w-3.5", !isDone && "opacity-0")} />
                    </button>
                    <div className="flex min-w-0 flex-col leading-tight">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{meta.status_lifecycle === "archived" ? "Archived" : isDone ? "Completed" : "Task"}</span>
                      {meta.related_id && <span className="truncate text-[10px] text-muted-foreground/70">Linked · {meta.related_type || "workflow"}</span>}
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setExpanded((v) => !v)} data-testid="drawer-expand" aria-label={expanded ? "Collapse" : "Expand"} className="hidden rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground sm:block">
                  {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </button>
                <button onClick={attemptClose} data-testid="drawer-close" aria-label="Close" className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><X className="h-5 w-5" /></button>
              </div>
            </header>

            {/* BODY */}
            <div className={cn("flex-1 space-y-6 overflow-y-auto scrollbar-thin p-5", expanded && "sm:mx-auto sm:w-full sm:max-w-3xl")}>
              {!isCreate && (
                <div className="flex items-center justify-end gap-2">
                  <label htmlFor="hide-empty" className="text-[11px] font-medium text-muted-foreground">Hide empty fields</label>
                  <Switch id="hide-empty" checked={hideEmpty} onCheckedChange={setHideEmpty} data-testid="drawer-hide-empty" />
                </div>
              )}

              <input value={f.title} onChange={(e) => { set("title", e.target.value); if (titleErr) setTitleErr(false); }} placeholder="Task title"
                data-testid="drawer-title" className={cn("w-full border-b bg-transparent pb-1.5 font-heading text-2xl font-semibold text-foreground placeholder:text-muted-foreground/40 focus:outline-none", titleErr ? "border-destructive" : "border-transparent focus:border-border")} />
              {titleErr && <p className="-mt-4 text-xs text-destructive">Please enter a title.</p>}

              {/* PRIMARY DETAILS */}
              <Section title="Primary details">
                {show("description") && <Field label="Notes"><Textarea rows={expanded ? 6 : 3} value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Add detail…" data-testid="drawer-notes" /></Field>}
                {canAssign && (
                  <Field label="Assignee"><Select value={f.owner_id || "self"} onValueChange={(v) => set("owner_id", v === "self" ? "" : v)}>
                    <SelectTrigger data-testid="drawer-assignee"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="self">Myself</SelectItem>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name || m.login_email}</SelectItem>)}</SelectContent>
                  </Select></Field>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Due date"><DatePicker value={f.due_date} onChange={(v) => set("due_date", v)} placeholder="Set due date" testid="drawer-due" /></Field>
                  <Field label="Priority"><Select value={f.priority} onValueChange={(v) => set("priority", v)}>
                    <SelectTrigger data-testid="drawer-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                  </Select></Field>
                </div>
                {show("due_time") && <Field label="Due time"><TimePicker value={f.due_time} onChange={(v) => set("due_time", v)} placeholder="Set time" testid="drawer-due-time" /></Field>}
                {show("category_id") && (cats.length === 0
                  ? <Field label="Category"><div data-testid="drawer-category-empty" className="flex h-9 items-center rounded-md border border-dashed border-border px-3 text-sm text-muted-foreground">No categories available</div></Field>
                  : <Field label="Category"><Select value={f.category_id || "none"} onValueChange={(v) => set("category_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="drawer-category"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">No category</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select></Field>)}
              </Section>

              {/* PLANNING */}
              {(boards.length > 0 || show("board_id")) && (
                <Section title="Planning &amp; context">
                  {boards.length > 0 && (
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Board"><Select value={f.board_id || "none"} onValueChange={(v) => setF((p) => ({ ...p, board_id: v === "none" ? "" : v, stage: "", stage_id: "" }))}>
                        <SelectTrigger data-testid="drawer-board"><SelectValue placeholder="None" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">No board</SelectItem>{boards.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                      </Select></Field>
                      {board && <Field label="Stage"><Select value={f.stage_id || board.stages?.[0]?.id || ""} onValueChange={(v) => set("stage_id", v)}>
                        <SelectTrigger data-testid="drawer-stage"><SelectValue /></SelectTrigger>
                        <SelectContent>{(board.stages || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select></Field>}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Reminder"><ReminderPicker value={f.reminders?.[0]?.at || ""} onChange={(v) => set("reminders", v ? [{ at: v }] : [])} testid="drawer-reminder" /></Field>
                    <Field label="Repeat"><Select value={f.recurrence?.freq || "none"} onValueChange={(v) => set("recurrence", v === "none" ? {} : { ...(f.recurrence || {}), freq: v, interval: f.recurrence?.interval || 1 })}>
                      <SelectTrigger data-testid="drawer-recurrence"><SelectValue placeholder="Does not repeat" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Does not repeat</SelectItem><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="yearly">Yearly</SelectItem></SelectContent>
                    </Select></Field>
                  </div>
                </Section>
              )}

              {/* WORK */}
              <Section title="Work & execution">
                <SubtaskList items={f.subtasks} onToggle={toggleSub} canEdit onAdd={addSub} onRemove={removeSub} onRename={renameSub} testid="drawer-subtask" />
                <button onClick={() => set("waiting", !f.waiting)} data-testid="drawer-waiting" className={cn("inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors", f.waiting ? "border-warning/50 bg-warning/10 text-warning" : "border-border text-muted-foreground hover:bg-muted")}>
                  <Clock className="h-3.5 w-3.5" /> {f.waiting ? "Waiting / blocked" : "Mark as waiting"}
                </button>
                {!isCreate && (meta.time_tracking || "off") !== "off" && (
                  <Field label="Time tracking"><div className="flex items-center gap-3"><TaskTimerControls task={meta} onChange={onChanged} /></div></Field>
                )}
                {show("estimate_minutes") && <Field label="Estimate (minutes)"><Input type="number" min="0" value={f.estimate_minutes} onChange={(e) => set("estimate_minutes", e.target.value)} placeholder="No estimate" data-testid="drawer-estimate" /></Field>}
              </Section>

              {/* MORE SETTINGS */}
              {show("tags") && (
                <Section title="More task settings">
                  <Field label="Tags"><TagInput value={f.tags || []} onChange={(v) => set("tags", v)} testid="drawer-tags" /></Field>
                </Section>
              )}

              {/* Add-field affordance when hiding empties */}
              {!isCreate && hideEmpty && hiddenKeys.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" data-testid="drawer-add-field"><Plus className="h-4 w-4" /> Add field</Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="start">{hiddenKeys.map((k) => <DropdownMenuItem key={k} onClick={() => reveal(k)} data-testid={`add-field-${k}`}>{LABELS[k]}</DropdownMenuItem>)}</DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* STICKY FOOTER */}
            <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-border bg-card/95 p-4 backdrop-blur-md">
              <div className="flex items-center gap-2">
                {!isCreate && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="gap-1.5" data-testid="drawer-more-actions"><MoreHorizontal className="h-4 w-4" /> More</Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      <DropdownMenuItem onClick={() => setConfirm("archive")} data-testid="action-archive"><Archive className="mr-2 h-4 w-4" /> Archive task</DropdownMenuItem>
                      <DropdownMenuItem onClick={doDuplicate} data-testid="action-duplicate"><Copy className="mr-2 h-4 w-4" /> Duplicate</DropdownMenuItem>
                      {canHardDelete && <><DropdownMenuSeparator /><DropdownMenuItem onClick={() => setConfirm("delete")} className="text-destructive focus:text-destructive" data-testid="action-delete"><Trash2 className="mr-2 h-4 w-4" /> Delete task</DropdownMenuItem></>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {dirty && !isCreate && <span className="text-[11px] text-warning" data-testid="drawer-unsaved-indicator">Unsaved changes</span>}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={attemptClose} data-testid="drawer-cancel">Cancel</Button>
                <Button size="sm" onClick={save} disabled={saving || (!isCreate && !dirty)} className="gap-1.5" data-testid="drawer-save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isCreate ? <PlusCircle className="h-4 w-4" /> : null}
                  {saving ? (isCreate ? "Creating…" : "Saving…") : isCreate ? "Create Task" : "Save Changes"}
                </Button>
              </div>
            </footer>
          </>
        )}
      </aside>

      <AlertDialog open={unsaved} onOpenChange={setUnsaved}>
        <AlertDialogContent data-testid="unsaved-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>You have unsaved changes on this task. Would you like to save them before leaving?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-between">
            <Button variant="ghost" onClick={() => setUnsaved(false)} data-testid="unsaved-keep">Keep editing</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={discardAndClose} data-testid="unsaved-discard">Discard changes</Button>
              <Button onClick={saveAndClose} disabled={saving} data-testid="unsaved-save">{saving ? "Saving…" : "Save changes"}</Button>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmDialog open={confirm === "archive"} onOpenChange={(o) => !o && setConfirm(null)} testid="archive-dialog" busy={busy} title="Archive this task?" description="This task will be removed from active views. You can restore it later." confirmLabel="Archive Task" onConfirm={doArchive} />
      <ConfirmDialog open={confirm === "delete"} onOpenChange={(o) => !o && setConfirm(null)} testid="delete-dialog" busy={busy} destructive title="Delete this task permanently?" description="This cannot be undone." confirmLabel="Delete Permanently" onConfirm={doDelete} />
    </div>,
    document.body
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-4 rounded-xl border border-border/70 bg-muted/20 p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/90">{title}</div>
      {children}
    </section>
  );
}
